import React, {
	useMemo,
	useState,
} from 'react';
import {
	Divider,
	Dropdown,
	Menu,
	Select,
	Space,
	Tag,
	Typography,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Moment } from 'moment';
import { useMutation, useQueryClient } from 'react-query';
import {
	AccountingItemApprovalStates,
	Currencies,
	DATE,
	ExpenseTypes,
	FixtureTypeLabels,
	FixtureTypes,
	TcInAllocationItemTypes,
} from '@shared/utils/constants';
import { formatCurrency } from '@shared/utils/currency';
import { toMoment } from '@shared/utils/date';
import { Values } from '@shared/utils/objectEnums';
import { standardSort } from '@shared/utils/standardSort';
import type { Port } from '@api/utils/ports';
import { GetVoyageExpensesResponse } from '@api/features/invoices/getVoyageExpenses';
import { GetDisbursementAccountsResponse } from '@api/features/disbursement-accounts/getDisbursementAccounts';
import { FixtureCounterpartyProps } from '@api/models/fixture-counterparty';
import { DisbursementAccountProps } from '@api/models/disbursement-account';
import {
	deleteDisbursementAccount,
	deleteVoyageExpense,
	submitDisbursementAccount,
	submitExpense,
	unsubmitDisbursementAccount,
	unsubmitExpense,
	updateTcInAllocationItem,
} from '@client/lib/api';
import Card from '@client/components/Card/Card';
import AddButton from '@client/components/AddButton';
import EditableTable from '@client/components/EditableTable';
import { formatDate } from '@client/utils/formatDate';
import EmptyText from '@client/components/EmptyText';
import CreateExpenseForm from '@client/components/CreateExpenseForm/CreateExpenseForm';
import DisbursementAccountModal from '@client/components/DisbursementAccountModal/DisbursementAccountModal';
import showErrorNotification from '@client/utils/showErrorNotification';
import ExpenseActionButtons from '@client/components/ExpenseDetailsCard/private/ExpenseActionButtons';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import ApprovalStateTag from '@client/components/ApprovalStateTag/ApprovalStateTag';
import { getFilterProps } from '@client/utils/table';
import { useVoyage } from './VoyageProvider/VoyageProvider';

export type VendorExpenseVisible = VendorExpenseSubTypes | EuExpenseSubTypes | boolean;
export type EuExpenseSubTypes = 'fuelEuPenalty' | 'euEts';
export type VendorExpenseSubTypes = 'basic' | 'portDA' | 'linerCost';
type ExpenseRow = GetDisbursementAccountsResponse[number] | GetVoyageExpensesResponse['rows'][number];

const VendorExpensesCard = ({
	allowCreatePortDA = true,
}: {
	allowCreatePortDA?: boolean;
}) => {
	const {
		fixtureCurrency,
		voyageDetails,
		vendorExpenses: allVendorExpenses,
		refreshDetails,
		refreshVendorExpenses,
		portDAs,
		allVoyages,
	} = useVoyage();

	const linkedOutVoyages = useMemo(() => (
		allVoyages?.filter((voyage) => (
			voyage.linkedTcInVoyageId === voyageDetails.id
		)) ?? []
	), [allVoyages, voyageDetails.id]);

	const queryClient = useQueryClient();

	const [
		vendorExpenseDrawerVisible, setVendorExpenseDrawerVisible,
	] = useState<VendorExpenseVisible>(false);

	const [editingVendorExpenseId, setEditingVendorExpenseId] = useState<number | null>(null);
	const [daModalOpen, setDaModalOpen] = useState(false);
	const [editingDaData, setEditingDataDa] = useState<GetDisbursementAccountsResponse[number]>();

	const submitDaMutation = useMutation(
		submitDisbursementAccount,
		{
			onSuccess: () => {
				showSuccessNotification('DA submitted for approval');
				queryClient.invalidateQueries('portDAs');
			}
		}
	);

	const submitVendorExpenseMutation = useMutation(
		submitExpense,
		{
			onSuccess: () => {
				showSuccessNotification('Expense submitted for approval');
				refreshVendorExpenses();
			},
		}
	);

	const unsubmitDaMutation = useMutation(
		unsubmitDisbursementAccount,
		{
			onSuccess: () => {
				showSuccessNotification('DA unsubmitted');
				queryClient.invalidateQueries('portDAs');
			}
		}
	);

	const unsubmitVendorExpenseMutation = useMutation(
		unsubmitExpense,
		{
			onSuccess: () => {
				showSuccessNotification('Expense unsubmitted');
				refreshVendorExpenses();
			}
		}
	);

	const onSubmitDA = (id: number) => submitDaMutation.mutate(id);
	const onSubmitExpense = (id: number) => submitVendorExpenseMutation.mutate(id);
	const onUnsubmitDA = (id: number) => unsubmitDaMutation.mutate(id);
	const onUnsubmitExpense = (id: number) => unsubmitVendorExpenseMutation.mutate(id);

	const vendorExpenses = useMemo(() => {
		if (allVendorExpenses == null) {
			return [];
		}

		return allVendorExpenses.rows;
	}, [allVendorExpenses]);

	const filteredVendorExpenses = vendorExpenses.filter(
		(ve) => !ve.isFuelEuPenalty && !ve.isEuEts && !ve.isUkEts,
	);

	const filteredExpensesWithDAs = useMemo(() => {
		return [
			...filteredVendorExpenses.map((v) => ({ ...v, key: `${v.id}-vendorExpense` })),
			...(portDAs ?? []).map((p) => ({ ...p, key: `${p.id}-da` }))
		];
	}, [filteredVendorExpenses, portDAs]);

	const deleteVendorExpense = async (expenseId: string, row: any) => {
		try {
			const id = expenseId.split('-')[0];

			if ('DACostItems' in row) {
				await deleteDisbursementAccount(Number(id));
				queryClient.invalidateQueries('portDAs');
			} else {
				await deleteVoyageExpense(Number(id));
			}

		} catch (e) {
			showErrorNotification('Error deleting vendor expense', e as Error);
		} finally {
			refreshVendorExpenses();
			refreshDetails();
			await queryClient.invalidateQueries(['itinerary', voyageDetails.id]);
			await queryClient.refetchQueries(['itinerary', voyageDetails.id]);
		}
	};

	const fixtureType = voyageDetails.fixture.type;

	let title = 'Vendor Expenses';

	if (fixtureType === FixtureTypes.SPOT) {
		title = 'Vendor Expenses, DA\'s & Liner Costs';
	}

	if (fixtureType === FixtureTypes.TC_OUT) {
		title = 'Vendor Expenses & DA\'s';
	}

	const editingVendorExpenses = useMemo(() => {
		const vendorExpense = vendorExpenses?.find((e) => e.id === editingVendorExpenseId);

		const allocation = voyageDetails.tcInAllocationItems.find((item) => (
			item.itemType === TcInAllocationItemTypes.VENDOR_EXPENSE &&
			item.itemId === editingVendorExpenseId
		));
	
		if (vendorExpense?.receivable != null) {
			return {
				...vendorExpense,
				allocatedToId: allocation?.allocatedToId ?? undefined,
				charterer: vendorExpense.receivable.chartererId,
				linerCost: {
					value: vendorExpense.linerCostPricePerUnit,
					currency: vendorExpense.currency,
					exchangeRate: vendorExpense.exchangeRate,
				},
			};
		}

		// Fallback
		if (vendorExpense == null) {
			return {
				id: null,
				charterer: null,
				linerCost: {
					value: 0,
					currency: Currencies.USD,
					exchangeRate: 1,
				},
				amount: 0,
				attachments: [],
				actions: [],
				cargoPortId: null,
				itemType: ExpenseTypes.PAYABLE,
				linerCostPricePerUnit: 0,
				value: 0,
				currency: Currencies.USD,
				exchangeRate: 1,
			};
		}

		return {
			...vendorExpense,
			allocatedToId: allocation?.allocatedToId ?? undefined,
			linerCost: vendorExpense?.linerCostPricePerUnit != null ?
				{
					value: vendorExpense.linerCostPricePerUnit,
					currency: vendorExpense.currency,
					exchangeRate: vendorExpense.exchangeRate,
				} :
				{
					value: 0,
					currency: Currencies.USD,
					exchangeRate: 1,
				},
		};
	}, [
		vendorExpenses,
		editingVendorExpenseId,
		voyageDetails.tcInAllocationItems,
	]);

	const columns = useMemo(() => {
		return [{
			title: 'Type',
			dataIndex: 'type',
		}, {
			title: 'Desc',
			dataIndex: 'note',
			render: (value: string, row: { note?: string; itemDescription?: string }) => {
				const valueToShow = row.note ?? row.itemDescription;

				if (!valueToShow) {
					return '-';
				}

				return (
					<Typography.Text
						ellipsis={{ tooltip: value }}
						style={{ maxWidth: 180 }}
					>
						{valueToShow}
					</Typography.Text>
				);
			},
		}, {
			title: 'Port',
			dataIndex: 'port',
			render: (port: Port) => port?.name ?? '-',
			sorter: standardSort(['port', 'name']),
			...getFilterProps(
				filteredExpensesWithDAs,
				'select',
				['port', 'name'],
			)
		}, {
			title: 'DA status',
			dataIndex: 'pdaFdaStatus',
			render: (status: string) => status ?? '-'
		}, {
			title: 'Supplier',
			dataIndex: 'supplier',
			render: (supplier: FixtureCounterpartyProps) => supplier?.name ?? '-',
			sorter: standardSort(['supplier', 'name']),
			...getFilterProps(
				filteredExpensesWithDAs,
				'select',
				['supplier', 'name'],
			)
		}, {
			title: 'Invoice date',
			dataIndex: 'invoiceDate',
			render: (date: Moment) => date ? toMoment(date).format(DATE) : '-'
		}, {
			title: 'Due date',
			dataIndex: 'dueDate',
			render: (date: Moment) => date ? toMoment(date).format(DATE) : '-'
		}, {
			title: 'Status',
			dataIndex: 'estimated',
			render: (estimated: boolean) => (
				<Tag color={estimated ? 'blue' : 'orange'}>{estimated ? 'Estimated' : 'Actual'}</Tag>
			) 
		}, {
			title: 'Amount',
			dataIndex: 'amount',
			render: (amount: number, row: DisbursementAccountProps) => formatCurrency(amount ?? 0, row.currency)
		}, {
			title: 'Paid',
			dataIndex: 'paid',
			render: (paid: number, row: DisbursementAccountProps) => formatCurrency(paid ?? 0, row.currency)
		},
		{
			title: 'Outstanding',
			dataIndex: 'outstanding',
			render: (outstanding: number, row: DisbursementAccountProps) => formatCurrency(outstanding ?? 0, row.currency)
		},
		...(fixtureType === FixtureTypes.TC_IN ? [{
			title: 'Allocate to voyage',
			dataIndex: 'allocation',
			width: 220,
			render: (_value: unknown, row: ExpenseRow) => {
				// DAs are not part of the Type 2 vendor-expense allocation
				if ('DACostItems' in row || 'costItems' in row) {
					return null;
				}

				const allocation = voyageDetails.tcInAllocationItems.find((item) => (
					item.itemType === TcInAllocationItemTypes.VENDOR_EXPENSE &&
					item.itemId === row.id
				));

				const selectedVoyageId = allocation?.allocatedToId ?? undefined;

				return (
					<Select
						allowClear
						placeholder="Select voyage"
						value={selectedVoyageId}
						style={{ width: '100%' }}
						status={selectedVoyageId == null ? 'error' : undefined}
						options={linkedOutVoyages.map((voyage) => ({
							value: voyage.id,
							label: `${voyage.identifier} - ${FixtureTypeLabels[voyage.fixtureType]}`,
						}))}
						onChange={async (newVoyageId?: number) => {
							const voyageId = newVoyageId ?? selectedVoyageId;

							if (voyageId == null) {
								return;
							}

							await updateTcInAllocationItem(
								voyageId,
								row.id,
								TcInAllocationItemTypes.VENDOR_EXPENSE,
								newVoyageId != null ? 100 : 0,
							);

							await refreshDetails();
						}}
					/>
				);
			},
		}] : []), 
		{
			title: 'State',
			dataIndex: 'state',
			render: (approvalState: Values<typeof AccountingItemApprovalStates>) => <ApprovalStateTag state={approvalState} />
		}, {
			title: '',
			dataIndex: 'approvalButtons',
			render: (_: any, row: ExpenseRow) => {
				const isDa = 'costItems' in row;

				const submitFn = isDa ?
					() => onSubmitDA(row.id) :
					() => onSubmitExpense(row.id);

				const unsubmitFn = isDa ?
					() => onUnsubmitDA(row.id) :
					() => onUnsubmitExpense(row.id);

				return (
					<ExpenseActionButtons
						expense={row}
						showTitle={false}
						onSubmitExpense={submitFn}
						onUnsubmitExpense={unsubmitFn}
					/>
				);
			}
		}];
	}, [
		onSubmitDA,
		onSubmitExpense,
		onUnsubmitDA,
		onUnsubmitExpense,
		fixtureType,
		linkedOutVoyages,
		voyageDetails.tcInAllocationItems,
		refreshDetails,
	]);

	return (
		<>
			<Card
				title={title}
				slim
				extra={(
					<Dropdown
						trigger={['click']}
						overlay={(
							<Menu>
								<Menu.Item
									key="basic"
									onClick={() => setVendorExpenseDrawerVisible('basic')}
								>
									Vendor Expense
								</Menu.Item>
								{allowCreatePortDA && (
									<Menu.Item
										key="portDA"
										onClick={() => setDaModalOpen(true)}
									>
										DA
									</Menu.Item>
								)}
							</Menu>
						)}
					>
						<AddButton
							key="createExpense"
							type="primary"
							icon={(<PlusOutlined />)}
						>
							Create expense
						</AddButton>
					</Dropdown>
				)}
			>
				{filteredExpensesWithDAs != null && (
					<EditableTable<any, 'key'>
						data-testid="vendorExpenses"
						keyDataIndex="key"
						editingRow={null}
						onSave={() => null}
						onDelete={deleteVendorExpense}
						onEditingRowChange={(key: string | null, row) => {
							if (!key) {
								return;
							}

							if ('DACostItems' in row) {
								setDaModalOpen(true);
								setEditingDataDa(row);

								return;
							}

							const id = Number(key.split('-')[0]);

							setEditingVendorExpenseId(id);

							let subType = 'basic';
							const expense = vendorExpenses?.find((v) => v.id === id);

							if (expense != null) {
								if (expense.isPortDA) {
									subType = 'portDA';
								}

								if (expense.isLinerCost) {
									subType = 'linerCost';
								}
							}

							setVendorExpenseDrawerVisible(subType as VendorExpenseVisible);
						}}
						size="small"
						pagination={false}
						enableEdit={(item) => item.state === AccountingItemApprovalStates.PENDING}
						enableDelete={(item) => item.state === AccountingItemApprovalStates.PENDING}
						deleteMessage={(item: ExpenseRow) => {
							if ('costItems' in item && item.syncedFromDis) {
								return (
									'Deleting this disbursement account from ClearVoyage will also cancel the ' +
									'port call and disbursement accounts in DA-Desk. Continue?'
								);
							}

							return 'Are you sure you want to delete this item?';
						}}
						iconButtons
						actionsTitle=""
						dataSource={filteredExpensesWithDAs}
						columns={columns}
						expandable={{
							expandedRowRender: (record) => (
								<Space direction="vertical">
									<Typography.Title level={5}>Internal note</Typography.Title>
									{record.note != null && record.note !== '' ? record.note : (
										<EmptyText />
									)}
									{record.customInvoiceId != null && (
										<>
											<b>Invoice ID:</b>
											{record.customInvoiceId}
										</>
									)}
									{record.invoiceDate != null && (
										<>
											<b>Invoice Date:</b>
											{formatDate(record.invoiceDate)}
										</>
									)}
									{record.dueDate != null && (
										<>
											<b>Due Date:</b>
											{formatDate(record.dueDate)}
										</>
									)}
									{record.currency !== fixtureCurrency && (
										<>
											<Divider />
											<b>Original Amount:</b>
											{` ${formatCurrency(record.amount, record.currency)}`}
											<br />
											<b>Exchange Rate:</b>
											{` ${record.exchangeRate} ${record.currency} = 1 ${fixtureCurrency} `}
										</>
									)}
								</Space>
							),
						}}
					/>
				)}
			</Card>
			<CreateExpenseForm
				open={vendorExpenseDrawerVisible}
				voyageId={voyageDetails.id}
				expenseData={
					editingVendorExpenses as Partial<Omit<GetVoyageExpensesResponse['rows'][number], 'charterer'> &
					{ charterer?: number | null }> | null
				}
				onClose={() => {
					setVendorExpenseDrawerVisible(false);
					setEditingVendorExpenseId(null);
					refreshVendorExpenses();
					refreshDetails();
				}}
			/>
			<DisbursementAccountModal
				onSave={() => {
					refreshDetails();
				}}
				mode={'edit'}
				open={daModalOpen}
				onClose={() => {
					setEditingDataDa(undefined);
					setDaModalOpen(false);
				}}
				voyageId={voyageDetails.id}
				disbursementAccount={editingDaData}
			/>
		</>
	);
};

export default VendorExpensesCard;
